import sanitizeHtml from 'sanitize-html';

import { MAIL_TEMPLATE_KEYS } from './mail-templates';
import {
  getRegistryEntry,
  isAuthTemplateKey,
  allowedVariableNames,
  type MailTemplateVariableDef,
} from './mail-template-registry';
import {
  getDefaultLayoutTitle,
  getDefaultTemplateContent,
} from './mail-template-defaults';
import {
  buildBrandedMailLayoutHtml,
  injectMailLayoutFooter,
  applyMailBrandToLayoutHtml,
  resolveMailLayoutHtml,
  mailFooterModeForTemplateKey,
  mailFooterPlainText,
  resolveMailBrandConfig,
  type MailBrandConfig,
} from './mail-layout-brand';

export type MailTemplateRow = {
  key: string;
  subject: string;
  htmlBody: string;
  textBody: string;
  version: number;
  usesLayout: boolean;
};

export type RenderedMail = {
  subject: string;
  html: string;
  text: string;
  templateKey: string;
  templateVersion: number;
  usedFallback: boolean;
};

export type MailTemplateRenderOptions = {
  key: string;
  variables: Record<string, unknown>;
  dbTemplate?: MailTemplateRow | null;
  layoutTemplate?: MailTemplateRow | null;
  strictMode?: boolean;
  brandConfig?: MailBrandConfig;
  onFallback?: (key: string, reason: string) => void;
};

export function escapeHtml(value: string) {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function escapePlain(value: string) {
  return value.replace(/\r/g, '');
}

function isSafeUrl(url: string) {
  try {
    const parsed = new URL(url, 'http://localhost');
    if (url.startsWith('/')) return true;
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
}

function formatVariable(value: unknown, type: MailTemplateVariableDef['type'], forHtml: boolean) {
  if (value === undefined || value === null) return '';
  if (type === 'multiline') {
    const text = escapePlain(String(value));
    return forHtml ? escapeHtml(text).replace(/\n/g, '<br/>') : text;
  }
  const text = String(value);
  if (type === 'url' && !isSafeUrl(text)) {
    throw new Error(`Unsafe URL variable: ${text}`);
  }
  return forHtml ? escapeHtml(text) : escapePlain(text);
}

export function validateTemplateVariables(
  key: string,
  variables: Record<string, unknown>,
): { ok: true } | { ok: false; errors: string[] } {
  const entry = getRegistryEntry(key);
  const errors: string[] = [];
  if (!entry) {
    if (!key.startsWith('custom.')) {
      errors.push(`Unknown template key: ${key}`);
    }
    return errors.length ? { ok: false, errors } : { ok: true };
  }

  const allowed = allowedVariableNames(key);
  if (entry) {
    for (const v of entry.variables) {
      const val = variables[v.name];
      if (v.required && (val === undefined || val === null || val === '')) {
        errors.push(`Missing required variable: ${v.name}`);
      }
    }
  }

  for (const name of Object.keys(variables)) {
    if (!allowed.has(name)) {
      errors.push(`Unknown variable: ${name}`);
    }
  }

  return errors.length ? { ok: false, errors } : { ok: true };
}

export function interpolateTemplate(
  template: string,
  key: string,
  variables: Record<string, unknown>,
  forHtml: boolean,
) {
  const entry = getRegistryEntry(key);
  const varMap = new Map(entry?.variables.map((v) => [v.name, v]) ?? []);

  return template.replace(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g, (_match, name: string) => {
    const def = varMap.get(name);
    const type = def?.type ?? 'string';
    return formatVariable(variables[name], type, forHtml);
  });
}

export function notificationPreferencesUrl(webBaseUrl: string) {
  return `${webBaseUrl.replace(/\/$/, '')}/settings/notifications/in-app`;
}

/** Appends manage-preferences footer for notification (non-auth) emails. */
export function appendNotificationPreferencesFooter(
  html: string,
  text: string,
  preferencesUrl: string,
) {
  const safeUrl = isSafeUrl(preferencesUrl) ? preferencesUrl : notificationPreferencesUrl('http://localhost:3000');
  const footerHtml = `<p style="margin:24px 0 0;font-size:12px;color:#888;">Manage notification preferences: <a href="${escapeHtml(safeUrl)}" style="color:#666;">${escapeHtml(safeUrl)}</a></p>`;
  const footerText = `\n\nManage notification preferences: ${safeUrl}`;

  const nextHtml = html.includes('</body>')
    ? html.replace('</body>', `${footerHtml}</body>`)
    : `${html}${footerHtml}`;

  return { html: nextHtml, text: `${text}${footerText}` };
}

export function mailLayoutInner(title: string, body: string, brandConfig?: MailBrandConfig) {
  const brand = brandConfig ?? resolveMailBrandConfig();
  const appUrl = brand.appUrl.replace(/\/$/, '');
  let html = buildBrandedMailLayoutHtml({ ...brand });
  html = interpolateTemplate(html, MAIL_TEMPLATE_KEYS.SYSTEM_LAYOUT, {
    title,
    bodyHtml: body,
    logoUrl: brand.logoUrl,
    appUrl,
  }, false);
  return applyMailBrandToLayoutHtml(html, brand);
}

function wrapWithLayout(
  key: string,
  innerHtml: string,
  subject: string,
  textBody: string,
  layoutTemplate: MailTemplateRow | null | undefined,
  brand: MailBrandConfig,
) {
  const title = getDefaultLayoutTitle(key);
  const appUrl = brand.appUrl.replace(/\/$/, '');
  const layoutVars = {
    title,
    bodyHtml: innerHtml,
    logoUrl: brand.logoUrl,
    appUrl,
  };

  const layoutHtml = resolveMailLayoutHtml(layoutTemplate?.htmlBody, brand);

  let html = interpolateTemplate(layoutHtml, MAIL_TEMPLATE_KEYS.SYSTEM_LAYOUT, layoutVars, false);

  html = applyMailBrandToLayoutHtml(html, brand);
  html = injectMailLayoutFooter(html, key, brand);
  const footerMode = mailFooterModeForTemplateKey(key);
  const prefs = brand.preferencesUrl ?? notificationPreferencesUrl(brand.appUrl);
  const text = `${textBody}${mailFooterPlainText(footerMode, brand.appUrl, prefs)}`;

  return { html, text, subject };
}

function isValidDbTemplate(row?: MailTemplateRow | null) {
  return !!(row?.subject?.trim() && row.htmlBody?.trim() && row.textBody?.trim());
}

export function renderMailTemplate(options: MailTemplateRenderOptions): RenderedMail {
  const { key, variables, strictMode, onFallback, brandConfig } = options;
  const brand = brandConfig ?? resolveMailBrandConfig();
  let usedFallback = false;
  let source: MailTemplateRow | null = options.dbTemplate ?? null;

  if (!isValidDbTemplate(source)) {
    if (strictMode && !isAuthTemplateKey(key)) {
      throw new Error(`Published template missing for ${key} (strict mode)`);
    }
    usedFallback = true;
    onFallback?.(key, source ? 'invalid_or_empty' : 'not_found');
    const defaults = getDefaultTemplateContent(key, variables);
    if (!defaults) {
      throw new Error(`No template or default for key: ${key}`);
    }
    source = {
      key,
      subject: defaults.subject,
      htmlBody: defaults.htmlBody,
      textBody: defaults.textBody,
      version: 0,
      usesLayout: getRegistryEntry(key)?.usesLayout ?? true,
    };
  }

  const validation = validateTemplateVariables(key, variables);
  if (!validation.ok) {
    throw new Error(validation.errors.join('; '));
  }

  let subject = interpolateTemplate(source!.subject, key, variables, false);
  const innerHtml = interpolateTemplate(source!.htmlBody, key, variables, true);
  let text = interpolateTemplate(source!.textBody, key, variables, false);

  const usesLayout = source!.usesLayout && key !== MAIL_TEMPLATE_KEYS.SYSTEM_LAYOUT;
  let html = innerHtml;

  if (usesLayout) {
    const wrapped = wrapWithLayout(key, innerHtml, subject, text, options.layoutTemplate, brand);
    html = wrapped.html;
    text = wrapped.text;
    subject = wrapped.subject;
  } else if (key === MAIL_TEMPLATE_KEYS.SYSTEM_LAYOUT) {
    const appUrl = brand.appUrl.replace(/\/$/, '');
    const layoutHtml = resolveMailLayoutHtml(innerHtml, brand);
    html = interpolateTemplate(
      layoutHtml,
      key,
      { ...variables, logoUrl: brand.logoUrl, appUrl },
      false,
    );
    html = applyMailBrandToLayoutHtml(html, brand);
    html = injectMailLayoutFooter(html, key, brand);
  }

  return {
    subject,
    html,
    text,
    templateKey: key,
    templateVersion: source!.version,
    usedFallback,
  };
}

const ADMIN_MAIL_ALLOWED_TAGS = [
  'a',
  'abbr',
  'address',
  'article',
  'aside',
  'b',
  'blockquote',
  'br',
  'caption',
  'center',
  'code',
  'col',
  'colgroup',
  'dd',
  'div',
  'dl',
  'dt',
  'em',
  'footer',
  'head',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'header',
  'hr',
  'html',
  'i',
  'img',
  'li',
  'main',
  'ol',
  'p',
  'pre',
  'section',
  'small',
  'span',
  'strong',
  'sub',
  'sup',
  'table',
  'tbody',
  'td',
  'tfoot',
  'th',
  'thead',
  'tr',
  'title',
  'u',
  'ul',
  'body',
] as const;

const ADMIN_MAIL_ALLOWED_ATTRIBUTES: sanitizeHtml.IOptions['allowedAttributes'] = {
  '*': ['class', 'dir', 'id', 'lang', 'role', 'style', 'title'],
  a: ['href', 'name', 'rel', 'target'],
  img: ['alt', 'height', 'src', 'width'],
  table: ['align', 'bgcolor', 'border', 'cellpadding', 'cellspacing', 'height', 'width'],
  col: ['align', 'span', 'valign', 'width'],
  colgroup: ['align', 'span', 'valign', 'width'],
  td: ['align', 'bgcolor', 'colspan', 'height', 'rowspan', 'valign', 'width'],
  th: ['align', 'bgcolor', 'colspan', 'height', 'rowspan', 'scope', 'valign', 'width'],
};

/** Explicit allowlist for administrator-authored email HTML. */
export function sanitizeAdminHtml(html: string) {
  const sanitized = sanitizeHtml(html, {
    allowedTags: [...ADMIN_MAIL_ALLOWED_TAGS],
    allowedAttributes: ADMIN_MAIL_ALLOWED_ATTRIBUTES,
    allowedSchemes: ['http', 'https', 'mailto', 'tel'],
    allowedSchemesAppliedToAttributes: ['href', 'src'],
    allowProtocolRelative: false,
    disallowedTagsMode: 'discard',
    enforceHtmlBoundary: true,
    parseStyleAttributes: true,
    transformTags: {
      a: (_tagName, attribs) => ({
        tagName: 'a',
        attribs: {
          ...attribs,
          ...(attribs.target === '_blank' ? { rel: 'noopener noreferrer' } : {}),
        },
      }),
    },
  });
  return sanitized
    .replace(/url\s*\(\s*(['"]?)\s*(?:javascript|data):[^)]*\)/gi, '')
    .replace(/expression\s*\([^)]*\)/gi, '');
}

export function extractTemplatePlaceholders(content: string): string[] {
  const matches = content.matchAll(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g);
  return [...new Set([...matches].map((m) => m[1]))];
}

/** Returns placeholder names in content that are not allowed for this template key. */
export function findUnknownTemplatePlaceholders(
  key: string,
  subject: string,
  htmlBody: string,
  textBody: string,
): string[] {
  const allowed = allowedVariableNames(key);
  if (key === MAIL_TEMPLATE_KEYS.SYSTEM_LAYOUT) {
    allowed.add('title');
    allowed.add('bodyHtml');
    allowed.add('logoUrl');
    allowed.add('appUrl');
  }
  const names = [
    ...extractTemplatePlaceholders(subject),
    ...extractTemplatePlaceholders(htmlBody),
    ...extractTemplatePlaceholders(textBody),
  ];
  return [...new Set(names)].filter((name) => !allowed.has(name));
}
